import clsx from 'clsx';
import {
  formatDuration,
  formatTimestamp,
  incidentTone,
} from '../lib/incident-helpers';
import type { Incident } from '../lib/incidents-api';

interface Props {
  incident: Incident;
  onClick: (id: number) => void;
}

function StatusIcon({
  tone,
  isMaintenance,
}: {
  tone: ReturnType<typeof incidentTone>;
  isMaintenance: boolean;
}) {
  if (isMaintenance) {
    return (
      <span
        aria-label="Maintenance"
        className={clsx(
          'inline-flex h-5 w-5 items-center justify-center rounded-full',
          tone === 'ok' && 'bg-ok text-white',
          tone === 'info' && 'bg-info text-white',
          tone === 'warn' && 'bg-warn text-white',
          tone === 'down' && 'bg-down text-white',
        )}
        title="Maintenance"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 11 11"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M5.5 1.5 L8.5 9.5 L2.5 9.5 Z" />
        </svg>
      </span>
    );
  }
  return (
    <span
      aria-label={tone === 'ok' ? 'Resolved' : 'Active outage'}
      className={clsx(
        'inline-flex h-5 w-5 items-center justify-center rounded-full text-white',
        tone === 'ok' && 'bg-ok',
        tone === 'warn' && 'bg-warn',
        tone === 'down' && 'bg-down',
        tone === 'info' && 'bg-info',
      )}
    >
      {tone === 'ok' ? (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 5.8 L4.5 8.3 L9 3.5" />
        </svg>
      ) : (
        <span className="text-[11px] font-bold leading-none">!</span>
      )}
    </span>
  );
}

export function IncidentRow({ incident, onClick }: Props) {
  const tone = incidentTone(incident);
  const subtitle = incident.resolvedAt
    ? `Resolved in ${formatDuration(incident.durationSec)} · ${formatTimestamp(incident.startedAt)}`
    : `Active · ${formatTimestamp(incident.startedAt)}`;

  return (
    <button
      type="button"
      onClick={() => {
        onClick(incident.id);
      }}
      className={clsx(
        'flex w-full items-start gap-3 rounded-md border border-line bg-paper-1 px-4 py-3 text-left',
        'transition-colors hover:border-line-strong',
        'focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ton-cyan))]/40',
      )}
    >
      <StatusIcon tone={tone} isMaintenance={incident.kind === 'maintenance'} />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-ink-1">
          {incident.title}
        </div>
        <div className="mt-1 text-[12px] text-ink-3 [font-feature-settings:'tnum']">
          {subtitle}
        </div>
      </div>
    </button>
  );
}
